import type { AnnotationsProps } from '@repo/image-annotator/lib';

/**
 * Shared request type for claim summary generation endpoints:
 * - POST /claim-summary/generate-print-view (PDF)
 * - POST /claim-summary/generate-pieces (PNGs/ZIP)
 *
 * Backend uses `GeneratePdfRequestDto` for both endpoints.
 */
export interface ClaimSummaryGenerateRequest {
  unifiedAppointmentId: string;
  attachmentID?: string;
  narrative?: string;
  includeClinicalNoteInAttachment?: boolean;
  clinicalNote?: string;
  fmxTitle?: string;
  toothLevelImages?: Array<{
    title: string;
    imageId: number;
    annotations?: AnnotationsProps;
    tableData: Array<{
      tooth: string;
      txCodes: string[];
      dmf: string;
      caries: boolean;
      resto: boolean;
    }>;
  }>;
  fmxImages?: Array<{
    imageId: number;
    annotations?: AnnotationsProps;
  }>;
  fmxTableData?: Array<{
    tooth: string;
    txCodes: string[];
    calculus: boolean;
    rblMesial: string | null;
    rblDistal: string | null;
  }>;
  includeFmxFindings?: boolean;
}

/**
 * Builds the request body for claim summary generation endpoints.
 * Conditionally includes fmxImages/fmxTableData only if they have items.
 */
export const buildClaimSummaryGenerateRequestBody = (
  request: ClaimSummaryGenerateRequest,
): Record<string, unknown> => {
  const includeClinicalNote =
    request.includeClinicalNoteInAttachment === true && request.clinicalNote;
  return {
    unifiedAppointmentId: request.unifiedAppointmentId,
    attachmentID: request.attachmentID,
    narrative: request.narrative,
    includeClinicalNoteInAttachment: request.includeClinicalNoteInAttachment,
    ...(includeClinicalNote && { clinicalNote: request.clinicalNote }),
    fmxTitle: request.fmxTitle,
    toothLevelImages: request.toothLevelImages,
    ...(request.fmxImages &&
      request.fmxImages.length > 0 && { fmxImages: request.fmxImages }),
    ...(request.fmxTableData &&
      request.fmxTableData.length > 0 && {
        fmxTableData: request.fmxTableData,
      }),
    includeFmxFindings: request.includeFmxFindings,
  };
};
